<template>
  <li v-if="isDivider" class="w-full py-1" aria-hidden="true">
    <div class="h-px w-full bg-gray-300" />
  </li>
  <li
    v-else
    ref="root"
    class="h-full flex flex-col justify-center group relative"
  >
    <component
      :is="actualComponent"
      :href="to"
      :aria-haspopup="items.length ? 'menu' : null"
      :aria-expanded="items.length ? String(isDropdownOpen) : null"
      class="flex flex-row gap-2 items-center h-full cursor-pointer transition-colors"
      :class="{
        'text-typo-primary hover:text-primary': type === 'default',
        'bg-primary hover:bg-primary-darker rounded-[4px] text-white hover:text-white px-2':
          type === 'primary',
        'text-white hover:text-gray-300': type === 'inverted',
        'py-2 max-lg:px-2': !$slots.default,
      }"
      @click.prevent="onClick"
    >
      <div v-if="icon" class="h-6 flex items-center">
        <IconComponent
          :icon="icon"
          :pack="iconPack"
          class="w-6"
          :class="{ 'animate-spin': icon === 'spinner' }"
        />
      </div>
      <img
        v-if="image"
        class="border-solid border-2 border-primary rounded-full inline-block w-6 h-6 align-middle"
        v-lazy-load :data-src="image"
        @error="$helpers.noImage"
      />
      <span v-if="text || $slots.default" class="truncate flex-1">
        <template v-if="text">{{ text }}</template>
        <template v-else>
          <slot />
        </template>
      </span>
      <IconComponent
        v-if="items.length"
        :icon="isDropdownOpen ? 'angle-up' : 'angle-down'"
        class="w-6"
      />
    </component>
    <ul
      v-if="items.length"
      class="lg:group-hover:flex flex-col flex-1 px-2 lg:absolute top-full max-lg:w-full bg-white lg:rounded-md lg:z-10 lg:border lg:border-gray-300 lg:shadow-md"
      :class="{
        flex: isDropdownOpen,
        hidden: !isDropdownOpen,
        'right-0': isLast,
        'left-0': !isLast,
      }"
    >
      <NavBarItem
        v-for="({ click, ...props }, index) in items"
        :key="props.id || index"
        v-bind="props"
        v-on="click ? { click } : {}"
        @click="closeDropdown"
      />
    </ul>
  </li>
</template>

<script>
import { computed, watch, onBeforeUnmount } from "vue";
import { ref, useContext, useRouter, useRoute } from "@nuxtjs/composition-api";
import IconComponent from "~/components/IconComponent.vue";

export default {
  name: "NavBarItem",
  components: { IconComponent },
  props: {
    isDivider: {
      type: Boolean,
      default: false,
    },
    id: {
      type: String,
      default: null,
    },
    to: {
      type: String,
      default: null,
    },
    icon: {
      type: String,
      default: null,
    },
    iconPack: {
      type: String,
      default: "fas",
    },
    image: {
      type: String,
      default: null,
    },
    type: {
      type: String,
      default: "default",
    },
    text: {
      type: String,
      default: "",
    },
    items: {
      type: Array,
      default: () => [],
    },
    isLast: {
      type: Boolean,
      default: false,
    },
    openByDefault: {
      type: Boolean,
      default: false,
    },
    forceReload: {
      type: Boolean,
      default: false,
    },
    target: {
      type: String,
      default: "_self",
    },
  },
  setup(props, { emit }) {
    const router = useRouter();
    const { $trackers } = useContext();
    const route = useRoute();
    const root = ref(null);
    const isDropdownOpen = ref(props.openByDefault);

    const closeDropdown = () => {
      isDropdownOpen.value = false;
    };

    const onDocumentClick = (event) => {
      if (root.value && !root.value.contains(event.target)) {
        closeDropdown();
      }
    };

    const onDocumentKeydown = (event) => {
      if (event.key === "Escape") {
        closeDropdown();
      }
    };

    const removeDocumentListeners = () => {
      document.removeEventListener("click", onDocumentClick);
      document.removeEventListener("keydown", onDocumentKeydown);
    };

    watch(
      isDropdownOpen,
      (isOpen) => {
        if (!process.client) {
          return;
        }
        if (isOpen) {
          document.addEventListener("click", onDocumentClick);
          document.addEventListener("keydown", onDocumentKeydown);
        } else {
          removeDocumentListeners();
        }
      },
      { immediate: true }
    );

    onBeforeUnmount(() => {
      if (process.client) {
        removeDocumentListeners();
      }
    });

    const onClick = () => {
      if (props.id) {
        $trackers.trackClick("NavBarItem", { itemId: props.id });
      }
      if (props.to) {
        if (props.target === "_blank") {
          window.open(props.to, "_blank", "noopener,noreferrer");
        } else if (props.forceReload) {
          window.location.href = props.to;
        } else {
          router.push(props.to);
        }
      }

      if (props.items.length) {
        isDropdownOpen.value = !isDropdownOpen.value;
      }

      emit("click");
    };

    watch(route, closeDropdown);

    const actualComponent = computed(() => (props.to ? "a" : "span"));
    return {
      root,
      onClick,
      closeDropdown,
      isDropdownOpen,
      actualComponent,
    };
  },
};
</script>
